import {gql} from '@apollo/client';
import {getTestProps} from '@benchling/js-test-utils';
import {Box, Checkbox, createStyles, Stack, Text, Title, Tooltip} from '@mantine/core';
import React, {useEffect, useMemo, useState} from 'react';

import ModelTypeConstants from 'constants/ModelTypeConstants';
import {LLMFeatureEnablementState} from 'graphql-client/__generated/graphqlGlobalTypes';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {ZINDEX_FLOATING_UI} from 'styles/StyleConstants';
import {LLMFeatureMetadata, LLM_FEATURE_METADATA} from 'tenants/admin/llm-features/LLMFeatureMetadata';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import {CenteredSpinner} from 'ui-library/Spinner';
import {getUserMessage} from 'util/Net';

import {
  RoutineLLMFeatureAccessQuery,
  RoutineLLMFeatureAccessQuery_routineLlmFeatureAccess_RoutineLLMFeatureAccessNode as FeatureAccessNode,
  RoutineLLMFeatureAccessQueryVariables,
} from './__generated/RoutineLLMFeatureAccessQuery';
import {
  RoutineServiceAccountMembershipQuery,
  RoutineServiceAccountMembershipQueryVariables,
} from './__generated/RoutineServiceAccountMembershipQuery';
import * as RoutinesDAO from './RoutinesDAO';
import {LLMFeatureAccessUpdate} from './RoutineTypes';
import {Principal, TeamMembershipCreateGuidance, TeamMembershipEditPanel} from './TeamMembership';

const useTeamAccessSectionStyles = createStyles((theme) => ({
  root: {
    paddingBottom: theme.spacing['3xl'],
    borderBottom: `1px solid ${theme.fn.rgba(theme.colors.gray[3], 0.5)}`,
  },
}));

export const ROUTINE_SERVICE_ACCOUNT_MEMBERSHIP_QUERY = gql`
  query RoutineServiceAccountMembershipQuery($routineId: StrictString!) @engComponent(name: LLM) @skipAudit {
    routine(id: $routineId) {
      id
      name
      principal {
        id
        name
        organizationRoles {
          organization {
            id
            name
            urlIdentifier
          }
        }
        teamRoles {
          role
          team {
            id
            name
            organization {
              id
              urlIdentifier
            }
          }
        }
      }
    }
  }
`;

function principalFromMembershipQuery(
  routine: NonNullable<RoutineServiceAccountMembershipQuery['routine']>
): Principal | null {
  const principal = routine.principal;
  if (principal == null) {
    return null;
  }
  return {
    id: principal.id,
    name: principal.name,
    organizationRoles: principal.organizationRoles,
    teamRoles: principal.teamRoles,
  };
}

function RoutineTeamMembershipAccessEdit({
  routineId,
  canEdit,
}: {
  routineId: string;
  canEdit: boolean;
}): JSX.Element {
  const membershipResult = useBenchlingQuery<
    RoutineServiceAccountMembershipQuery,
    RoutineServiceAccountMembershipQueryVariables
  >(ROUTINE_SERVICE_ACCOUNT_MEMBERSHIP_QUERY, {
    variables: {routineId},
    fetchPolicy: 'cache-and-network',
    renderCachedDataWhenRefetching: true,
  });

  const principal = useMemo((): Principal | null => {
    if (membershipResult.type !== QueryResultType.SUCCESS || membershipResult.data.routine == null) {
      return null;
    }
    return principalFromMembershipQuery(membershipResult.data.routine);
  }, [membershipResult]);

  const hasError =
    membershipResult.type === QueryResultType.ERROR ||
    (membershipResult.type === QueryResultType.SUCCESS && membershipResult.data.routine == null);

  return (
    <TeamMembershipEditPanel
      canEdit={canEdit}
      hasError={hasError}
      isLoading={membershipResult.type === QueryResultType.PENDING}
      principal={principal}
      principalId={routineId}
    />
  );
}

export const ROUTINE_LLM_FEATURE_ACCESS_QUERY = gql`
  query RoutineLLMFeatureAccessQuery($routineId: StrictString) @engComponent(name: LLM) @skipAudit {
    routineLlmFeatureAccess(routineId: $routineId) {
      configKey
      enablementState
      isEnabledForCurrentUser
      isEnabledForRoutine
      recommendedDefaultForNewAgent
    }
  }
`;

type RoutineDataAccessProps =
  | {
      mode: 'create';
      updates: Array<LLMFeatureAccessUpdate> | null;
      onUpdatesChange: (updates: Array<LLMFeatureAccessUpdate>) => void;
      onReadyChange: (ready: boolean) => void;
    }
  | {
      mode: 'edit';
      routineId: string;
      canEdit: boolean;
    };

function getDisabledReason(feature: FeatureAccessNode): string | null {
  if (feature.enablementState === LLMFeatureEnablementState.ALL_USERS) {
    return 'This feature is enabled for all users and agents and cannot be disabled.';
  }
  if (feature.enablementState === LLMFeatureEnablementState.UNAVAILABLE) {
    return 'This feature is currently disabled for all users and agents and cannot be enabled.';
  }
  if (!feature.isEnabledForCurrentUser) {
    return 'This feature is not yet enabled for you, so you are restricted from editing this value for your agent.';
  }
  return null;
}

function shouldEnableByDefault(feature: FeatureAccessNode): boolean {
  return (
    feature.enablementState === LLMFeatureEnablementState.SOME_USERS &&
    feature.isEnabledForCurrentUser &&
    feature.recommendedDefaultForNewAgent
  );
}

function RoutineAIAccessHeader(): JSX.Element {
  return (
    <Stack spacing="xs">
      <Title order={4}>AI Access</Title>
      <Text size="sm" color="dimmed">
        Choose which AI capabilities this Agent can use.
      </Text>
    </Stack>
  );
}

function RoutineLLMFeatureAccess(props: RoutineDataAccessProps): JSX.Element {
  const routineId = props.mode === 'edit' ? props.routineId : null;
  const queryResult = useBenchlingQuery<RoutineLLMFeatureAccessQuery, RoutineLLMFeatureAccessQueryVariables>(
    ROUTINE_LLM_FEATURE_ACCESS_QUERY,
    {variables: {routineId}, renderCachedDataWhenRefetching: true}
  );
  const queriedFeatures =
    queryResult.type === QueryResultType.SUCCESS ? queryResult.data.routineLlmFeatureAccess : null;
  const [pendingConfigKey, setPendingConfigKey] = useState<string | null>(null);
  const [savedEnabledByConfigKey, setSavedEnabledByConfigKey] = useState<Record<string, boolean>>({});
  const [loadedFeatures, setLoadedFeatures] = useState<Array<FeatureAccessNode> | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (queriedFeatures != null) {
      setLoadedFeatures(queriedFeatures);
    }
  }, [queriedFeatures]);

  useEffect(() => {
    if (props.mode !== 'create') {
      return;
    }
    if (queryResult.type !== QueryResultType.SUCCESS) {
      props.onReadyChange(false);
      return;
    }
    if (props.updates == null) {
      const recommendedUpdates = queryResult.data.routineLlmFeatureAccess
        .filter((feature) => shouldEnableByDefault(feature))
        .map((feature) => ({configKey: feature.configKey, enabled: true}));
      props.onUpdatesChange(recommendedUpdates);
    }
    props.onReadyChange(true);
  }, [props, queryResult]);

  const features = queriedFeatures ?? loadedFeatures;
  if (queryResult.type === QueryResultType.ERROR && features == null) {
    return (
      <Stack spacing="md" {...getTestProps({component: 'RoutineDataAccess', element: 'ai-access'})}>
        <RoutineAIAccessHeader />
        <InlineBanner
          bannerType={InlineBannerType.ERROR}
          text="Could not load AI access"
          supportingInfo="Try reloading the page."
        />
      </Stack>
    );
  }
  if (features == null) {
    return (
      <Stack spacing="md" {...getTestProps({component: 'RoutineDataAccess', element: 'ai-access'})}>
        <RoutineAIAccessHeader />
        <CenteredSpinner aria-label="Loading AI access" />
      </Stack>
    );
  }

  const handleChange = async (configKey: string, enabled: boolean): Promise<void> => {
    const update = {configKey, enabled};
    if (props.mode === 'create') {
      const otherUpdates = (props.updates ?? []).filter((candidate) => candidate.configKey !== configKey);
      props.onUpdatesChange([...otherUpdates, update]);
      return;
    }

    setError(null);
    setPendingConfigKey(configKey);
    const mutationError = await RoutinesDAO.updateRoutine(props.routineId, {
      llmFeatureAccess: [update],
    }).then(
      () => null,
      (caughtError: unknown) => caughtError
    );
    if (mutationError != null) {
      setError(getUserMessage(mutationError) ?? 'Could not update AI access. Try again.');
      setPendingConfigKey(null);
      return;
    }

    setSavedEnabledByConfigKey((current) => ({...current, [configKey]: enabled}));
    const refreshSucceeded = await queryResult.refetch().then(
      () => true,
      () => false
    );
    if (refreshSucceeded) {
      setSavedEnabledByConfigKey((current) => {
        const next = {...current};
        delete next[configKey];
        return next;
      });
    } else {
      setError('AI access was updated, but the latest state could not be loaded. Reload the page.');
    }
    setPendingConfigKey(null);
  };

  return (
    <Stack spacing="md" {...getTestProps({component: 'RoutineDataAccess', element: 'ai-access'})}>
      <RoutineAIAccessHeader />
      {error != null && <InlineBanner bannerType={InlineBannerType.ERROR} text={error} />}
      <Stack spacing="sm">
        {features
          .filter((feature) => feature.enablementState !== LLMFeatureEnablementState.UNAVAILABLE)
          .map((feature) => {
            const update =
              props.mode === 'create'
                ? props.updates?.find((candidate) => candidate.configKey === feature.configKey)
                : null;
            const defaultChecked =
              props.mode === 'create'
                ? shouldEnableByDefault(feature)
                : (savedEnabledByConfigKey[feature.configKey] ?? feature.isEnabledForRoutine);
            const checked =
              feature.enablementState === LLMFeatureEnablementState.ALL_USERS ||
              (update?.enabled ?? defaultChecked);
            const featureDisabledReason = getDisabledReason(feature);
            const cannotEditAgent = props.mode === 'edit' && !props.canEdit;
            const disabled = featureDisabledReason != null || pendingConfigKey != null || cannotEditAgent;
            const disabledReason =
              featureDisabledReason ??
              (cannotEditAgent ? 'You do not have permission to edit this agent.' : null);
            const metadata: LLMFeatureMetadata = LLM_FEATURE_METADATA[feature.configKey] ?? {
              name: feature.configKey,
              overview: 'Configure access to this AI capability.',
            };
            const disabledReasonId =
              disabledReason == null ? undefined : `routine-ai-access-${feature.configKey}-disabled-reason`;
            return (
              <React.Fragment key={feature.configKey}>
                <Tooltip
                  label={disabledReason}
                  disabled={disabledReason == null}
                  withinPortal={true}
                  zIndex={ZINDEX_FLOATING_UI}
                  // Position the tooltip to fit in the navigation gutter.
                  width={200}
                  multiline={true}
                  position="left-start"
                >
                  <Box
                    aria-label={disabledReason == null ? undefined : `${metadata.name}: ${disabledReason}`}
                    tabIndex={disabledReason == null ? undefined : 0}
                  >
                    <Checkbox
                      key={feature.configKey}
                      styles={(theme) => ({
                        // Override nucleus-ui style, whose alignSelf: 'center' causes horizontal centering
                        // when the label is followed by a wider description.
                        label: {
                          alignSelf: 'flex-start',
                          paddingLeft: theme.spacing.md,
                          fontSize: theme.fontSizes.md,
                          fontWeight: theme.other.fontWeights.semibold,
                        },
                        // Improve top alignment between checkbox and label.
                        labelWrapper: {transform: 'translateY(-1px)'},
                        inner: {alignSelf: 'flex-start'},
                        // When checkbox is disabled, the label and description are both gray, so make the
                        // description 1px smaller to help visually delineate.
                        description: {fontSize: '12px', paddingLeft: theme.spacing.md},
                      })}
                      checked={checked}
                      disabled={disabled}
                      aria-describedby={disabledReasonId}
                      label={metadata.name}
                      description={metadata.overview}
                      onChange={(event) => {
                        handleChange(feature.configKey, event.currentTarget.checked);
                      }}
                    />
                    {disabledReason != null && (
                      <Text className="sr-only" component="span" id={disabledReasonId}>
                        {disabledReason}
                      </Text>
                    )}
                  </Box>
                </Tooltip>
              </React.Fragment>
            );
          })}
      </Stack>
    </Stack>
  );
}

export default function RoutineDataAccess(props: RoutineDataAccessProps): JSX.Element {
  const {classes} = useTeamAccessSectionStyles();
  return (
    <Stack spacing="xl" {...getTestProps({component: 'RoutineDataAccess', element: 'root'})}>
      <Box className={classes.root}>
        {props.mode === 'create' ? (
          <TeamMembershipCreateGuidance principalType={ModelTypeConstants.ROUTINE} />
        ) : (
          <RoutineTeamMembershipAccessEdit routineId={props.routineId} canEdit={props.canEdit} />
        )}
      </Box>
      <RoutineLLMFeatureAccess {...props} />
    </Stack>
  );
}
